import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function TabsContentSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const tabs = (content.tabs || []).map(tab => ({ ...tab, ...resolveItemMedia(tab, mediaMode, mediaFolders) }));
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const [activeTab, setActiveTab] = useState(0);
    const variant = config.variant || 'horizontal';

    if (tabs.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const tab = tabs[activeTab];

    // Vertical tabs
    if (variant === 'vertical') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="md:w-1/4 flex md:flex-col gap-2">
                            {tabs.map((t, i) => (
                                <button key={i} onClick={() => setActiveTab(i)}
                                    className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-left transition-all ${i === activeTab ? 'text-white shadow-lg' : 'hover:opacity-80'}`}
                                    style={i === activeTab ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb', color: s.textSub }}>
                                    {t.icon && <span>{t.icon}</span>}
                                    {t.title}
                                </button>
                            ))}
                        </div>
                        <div className={`md:w-3/4 ${card} p-6`}>
                            {tab && (
                                <div key={activeTab}>
                                    {(tab.resolvedImage || tab.carouselImages || tab.video) && (
                                        <div className="mb-4"><MediaRenderer item={tab} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-video" rounded="rounded-xl" /></div>
                                    )}
                                    <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{tab.heading || tab.title}</h3>
                                    <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{tab.body}</p>
                                    {tab.cta_text && <a {...resolveLinkProps(tab.cta_link || '#')} className="inline-block mt-4 text-sm font-semibold hover:underline" style={{ color: s.accent }}>{tab.cta_text}</a>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // Horizontal (default)
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="flex flex-wrap justify-center gap-2 mb-8">
                    {tabs.map((t, i) => (
                        <button key={i} onClick={() => setActiveTab(i)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${i === activeTab ? 'text-white shadow-lg' : 'hover:opacity-80'}`}
                            style={i === activeTab ? { backgroundColor: s.accent } : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>
                            {t.icon && <span>{t.icon}</span>}
                            {t.title}
                        </button>
                    ))}
                </div>
                {tab && (
                    <div className={`${card} p-8 max-w-3xl mx-auto transition-all duration-300`} key={activeTab}>
                        {(tab.resolvedImage || tab.carouselImages || tab.video) && (
                            <div className="mb-4"><MediaRenderer item={tab} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-video" rounded="rounded-xl" /></div>
                        )}
                        <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{tab.heading || tab.title}</h3>
                        <p className="text-base leading-relaxed" style={{ color: s.textSub }}>{tab.body}</p>
                        {tab.cta_text && <a {...resolveLinkProps(tab.cta_link || '#')} className="inline-flex items-center gap-1 mt-4 text-sm font-semibold hover:underline" style={{ color: s.accent }}>{tab.cta_text}</a>}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
